import { StorageService } from './../../../common/services/storage.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';

import { GrantService } from '../../../common/services/grant.service';
import { Grant } from '../../../common/models/grant';
import { CurrentUser } from '../../../common/models/user';
import { EditorsComponent } from "../../../common/components/editor/editor.component";
import { Base64File } from '../../../common/models/file';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { FileUploadControl, FileUploadModule } from '@iplab/ngx-file-upload';
import { MatCardModule } from '@angular/material/card';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-grant',
    templateUrl: './grant.component.html',
    styleUrls: ['./grant.component.scss'],
    imports: [
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatDatepickerModule,
    MatIconModule,
    ReactiveFormsModule,
    EditorsComponent,
    FileUploadModule
]
})
export class GrantComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  grantForm: FormGroup;
  isEditMode = false;
  user: CurrentUser | null = null;
  selectedFiles: Base64File[] = [];
  grantId: string | null = null;

  fileUploadControl = new FileUploadControl();

  isToggled = false;

  get descriptionControl(): FormControl {
    return this.grantForm.get('description') as FormControl;
  }

  private readonly snackbarConfig: MatSnackBarConfig = {
    duration: 3000,
    horizontalPosition: 'right',
    verticalPosition: 'top'
  };

  constructor(
    public themeService: CustomizerSettingsService,
    private fb: FormBuilder,
    private grantService: GrantService,
    private storageService: StorageService,
    private snackBar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.themeService.isToggled$.pipe(takeUntil(this.destroy$)).subscribe(isToggled => {
      this.isToggled = isToggled;
    });

    this.grantForm = this.fb.group({
      name: ['', Validators.required],
      description: ['', Validators.required],
      year: ['', [Validators.required, Validators.minLength(4)]],
      validUntil: ['', Validators.required],
    });

    this.fileUploadControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((files: File[]) => {
      this.selectedFiles = files.map(file => ({
        fileName: file.name,
        contentType: file.type,
        base64Content: ''
      }));

      files.forEach((file, index) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (reader.result) {
            this.selectedFiles[index].base64Content = (reader.result as string).split(',')[1];
          }
        };
        reader.readAsDataURL(file);
      });
    });
  }

  ngOnInit(): void {
    this.storageService.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.user = user;
    });

    // Determine if we are in edit mode
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.grantId = params.get('id');
      if (this.grantId) {
        this.isEditMode = true;
        this.loadGrantData(this.grantId);
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadGrantData(grantId: string): void {
    this.grantService.getGrant(grantId).pipe(takeUntil(this.destroy$)).subscribe((grant: Grant) => {
      this.grantForm.patchValue({
        name: grant.name,
        description: grant.description,
        year: grant.year,
        validUntil: grant.validUntil
      });

      if (grant.files) {
        grant.files.forEach((file: Base64File) => {
          this.selectedFiles.push({
            fileName: file.fileName,
            contentType: file.contentType,
            base64Content: file.base64Content
          });

          // Update the file upload control
          this.fileUploadControl.setValue([
            ...this.fileUploadControl.value,
            new File(
              [this.base64ToBlob(file.base64Content, file.contentType)],
              file.fileName,
              { type: file.contentType }
            )
          ]);
        });
      }
    });
  }

  // Helper method to convert Base64 string to Blob
  base64ToBlob(base64: string, contentType = ''): Blob {
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files) {
      Array.from(input.files).forEach(file => {
        const reader = new FileReader();
        reader.onload = () => {
          if (reader.result) {
            this.selectedFiles.push({
              fileName: file.name,
              contentType: file.type,
              base64Content: (reader.result as string).split(',')[1]
            });
          }
        };
        reader.readAsDataURL(file);
      });
    }
  }

  removeFile(file: Base64File): void {
    this.selectedFiles = this.selectedFiles.filter(f => f.fileName !== file.fileName);
    // Update the fileUploadControl
    const updatedFiles = this.fileUploadControl.value.filter((f: File) => f.name !== file.fileName);
    this.fileUploadControl.setValue(updatedFiles);
  }

  onSubmit() {
    if (this.grantForm.valid) {
      if (this.isEditMode && this.grantId) {
        this.updateGrant();
      } else {
        this.registerGrant();
      }
    }
  }

  registerGrant() {
    if (this.user) {
      const createGrantDto = {
        ...this.grantForm.value,
        municipalityId: this.user.municipalityId,
        files: this.selectedFiles
      };

      this.grantService.createGrant(createGrantDto).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.snackBar.open('Uspješno kreiran natječaj.', 'Zatvori', this.snackbarConfig);
          this.router.navigate(['/municipality/grants']);
        },
        error: () => {
          this.snackBar.open('Greška prilikom kreiranja natječaja. Pokušajte ponovno.', 'Zatvori', this.snackbarConfig);
        }
      });
    }
  }

  updateGrant() {
    const updatedData: Grant = {
      ...this.grantForm.value,
      id: this.grantId,
      files: this.selectedFiles
    };

    this.grantService.updateGrant(this.grantId!, updatedData).subscribe({
      next: () => {
        this.snackBar.open('Natječaj uspješno ažuriran.', 'Zatvori', this.snackbarConfig);
        this.router.navigate(['/municipality/grants']);
      },
      error: () => {
        this.snackBar.open('Greška prilikom ažuriranja natječaja. Pokušajte ponovno.', 'Zatvori', this.snackbarConfig);
      }
    });
  }
}